import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  DefaultActions,
  FulfilledAction,
  PendingAction,
  RejectedAction,
  StatusSelectors,
} from '../../common/redux/DeafaultActions';
import { ListShipParams, listShips } from './homeAPI';
import { RootState } from '../../app/store';
import RequestData from '../../common/data-types/request-data';
import RequestStatus from '../../common/enums/request-status.enum';
import ListShipData from '../../common/data-types/ship-list-data';

const {
  isDefaultPendingAction,
  isDefaultRejectedAction,
  isDefaultFulfilledAction,
} = DefaultActions;

const { selectErrorByType, selectIsFulfilledByType, selectIsRequestingByType } =
  StatusSelectors;

const isFulfilledAction = (action: AnyAction): action is FulfilledAction =>
  isDefaultFulfilledAction(action, 'home', '/fulfilled');

const isPendingAction = (action: AnyAction): action is PendingAction =>
  isDefaultPendingAction(action, 'home', '/pending');

const isRejectedAction = (action: AnyAction): action is RejectedAction =>
  isDefaultRejectedAction(action, 'home', '/rejected');

// #region Thunks
const LIST_SHIPS_THUNK = 'home/list';
export const listShipsThunk = createAsyncThunk(
  LIST_SHIPS_THUNK,
  async ({ first, offset, filters, shipName }: ListShipParams) => {
    const data = await listShips({ first, offset, filters, shipName });

    return data;
  }
);
// #endregion Thunks

// #region Slice
interface HomeState {
  list: ListShipData;
  requestIds: RequestData['id'][];
  requestsById: Record<RequestData['id'], RequestData>;
}

const initialState: HomeState = {
  list: { list: [], total: 0 },
  requestIds: [],
  requestsById: {},
};

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listShipsThunk.fulfilled, (state, action) => {
        state.list = action.payload;
      })
      .addMatcher(isFulfilledAction, (state, action: AnyAction) => {
        const { meta } = action;
        const request = state.requestsById[meta.requestId];

        Object.assign(request, {
          error: undefined,
          status: RequestStatus.Succeeded,
          type: action.type,
        });
      })
      .addMatcher(isPendingAction, (state, action: AnyAction) => {
        const { meta, type } = action;
        const { requestId } = meta;

        state.requestIds.push(requestId);

        const request: RequestData = {
          id: requestId,
          status: RequestStatus.Loading,
          type,
        };

        state.requestsById[requestId] = request;
      })
      .addMatcher(isRejectedAction, (state, action: AnyAction) => {
        const { error, meta, type } = action;
        const { requestId } = meta;

        state.requestIds.push(requestId);

        const request: RequestData = {
          id: requestId,
          status: RequestStatus.Failed,
          type,
          error,
        };

        state.requestsById[requestId] = request;
      });
  },
});
// #endregion Slice

// #region Selectors
// #endregion Private Selectors
const selectHomeIsFulfilledByType = (
  rootState: RootState,
  type: RequestData['type']
): boolean => {
  return selectIsFulfilledByType({
    requestState: selectHomeRequestState(rootState),
    type,
  });
};

const selectHomeIsRequestingByType = (
  rootState: RootState,
  type: RequestData['type']
): boolean => {
  return selectIsRequestingByType({
    requestState: selectHomeRequestState(rootState),
    type,
  });
};

const selectHomeErrorByType = (
  rootState: RootState,
  type: RequestData['type']
): Record<string, any> | null => {
  return selectErrorByType({
    requestState: selectHomeRequestState(rootState),
    type,
  });
};

const selectHomeRequestState = (
  rootState: RootState
): Pick<HomeState, 'requestIds' | 'requestsById'> => {
  const state = selectHomeState(rootState);

  return {
    requestIds: state.requestIds,
    requestsById: state.requestsById,
  };
};

const selectHomeState = (rootState: RootState): HomeState => rootState.home;
// #region Private Selectors

// #region Public Selectors
export const selectHomeIsFulfilledList = (rootState: RootState): boolean => {
  return selectHomeIsFulfilledByType(rootState, LIST_SHIPS_THUNK);
};

export const selectHomeIsRequestingList = (rootState: RootState): boolean => {
  return selectHomeIsRequestingByType(rootState, LIST_SHIPS_THUNK);
};

export const selectShipList = (rootState: RootState): ListShipData => {
  const state = selectHomeState(rootState);
  return state.list;
};
// #endregion Public Selectors
// #endregion Selectors

export const homeReducer = homeSlice.reducer;
